import React from 'react';
import { Form, Select, Input, Button, Tooltip, Typography, ResourceGroupSelector } from '../../../../components';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { FormIntroduction } from '../components/FormIntroduction';
import { InstanceTagsLabel } from '../components/InstanceTagsLabel';
import { LoadingState } from '../components/LoadingState';
import { CloudAttributesError } from '../components/CloudAttributesError';
import { CommonApplianceConfiguration } from '../components/CommonApplianceConfiguration';
import { VMBasedMigrationsConfig } from '../components/VMBasedMigrationsConfig';
import { useAzureLocalApplianceSetup } from './useAzureLocalApplianceSetup';
import { AzureLocalNetworkConfig } from './AzureLocalNetworkConfig';
import { KeyValuePairs } from '../components/KeyValuePairs';

const { Title } = Typography;

export const AzureLocalApplianceSetup = ({ cloudAccount, onSubmit, onCancel, submitting, isRedeploy }) => {
    const {
        form,
        initialLoading,
        loadingError,
        loadCloudAttributes,
        loadingAttributes,
        loadingLocationData,
        subscription,
        location,
        virtualNetwork,
        resourceGroups,
        selectedLogicalNetwork,
        instanceTags,
        setInstanceTags,
        handleSubscriptionChange,
        handleLocationChange,
        handleVirtualNetworkChange,
        handleFormSubmit,
        subscriptionOptions,
        customLocationOptions,
        storagePathOptions,
        logicalNetworkOptions,
        commonConfigProps,
    } = useAzureLocalApplianceSetup({ cloudAccount, onSubmit, isRedeploy });

    if (initialLoading) {
        return <LoadingState />;
    }

    if (loadingError) {
        return (
            <div className="azure-local-appliance-setup">
                <FormIntroduction />
                <CloudAttributesError error={loadingError} onRetry={loadCloudAttributes} />
            </div>
        );
    }

    return (
        <div className="azure-local-appliance-setup">
            <FormIntroduction />
            <Form form={form} layout="vertical" onFinish={handleFormSubmit} initialValues={{ ipType: 'dhcp', useProxyEnabled: true }}>
                <Title level={4}>Azure Local Account Setup</Title>

                <Form.Item
                    label="Subscription"
                    name="subscription"
                    rules={[{ required: true, message: 'Please select a subscription' }]}
                >
                    <Select
                        placeholder="Select a Subscription"
                        onChange={handleSubscriptionChange}
                        loading={loadingAttributes}
                        showSearch={{ optionFilterProp: 'label' }}
                        options={subscriptionOptions}
                    />
                </Form.Item>

                <Form.Item
                    label="Location"
                    name="location"
                    rules={[{ required: true, message: 'Please select a location' }]}
                >
                    <Select
                        placeholder="Select a Location"
                        onChange={handleLocationChange}
                        disabled={!subscription || loadingLocationData}
                        loading={loadingLocationData}
                        showSearch={{ optionFilterProp: 'label' }}
                        options={customLocationOptions}
                    />
                </Form.Item>

                {loadingLocationData && <div className="loading-indicator">Loading attributes...</div>}

                <Form.Item
                    label="Resource Group"
                    name="resourceGroup"
                    rules={[{ required: true, message: 'Please enter or select a resource group' }]}
                >
                    <ResourceGroupSelector
                        resourceGroups={resourceGroups}
                        loading={loadingLocationData}
                        disabled={!subscription || loadingLocationData}
                    />
                </Form.Item>

                <Form.Item label="Storage">
                    <span>32 GiB</span>
                </Form.Item>

                <Form.Item
                    label={
                        <span>
                            Storage Path{' '}
                            <Tooltip title="The Storage Path determines where the Migration Appliance VM disk will reside.">
                                <QuestionCircleOutlined />
                            </Tooltip>
                        </span>
                    }
                    name="storagePath"
                    rules={[{ required: true, message: 'Please select a storage path' }]}
                >
                    <Select
                        placeholder="Select Storage Path"
                        disabled={!location || loadingLocationData}
                        showSearch={{ optionFilterProp: 'label' }}
                        options={storagePathOptions}
                    />
                </Form.Item>

                <Form.Item
                    label="Virtual Network"
                    name="virtualNetwork"
                    rules={[{ required: true, message: 'Please select a virtual network' }]}
                >
                    <Select
                        placeholder="Select a Virtual Network"
                        onChange={handleVirtualNetworkChange}
                        disabled={!location || loadingLocationData}
                        showSearch={{ optionFilterProp: 'label' }}
                        options={logicalNetworkOptions}
                    />
                </Form.Item>

                <AzureLocalNetworkConfig
                    network={selectedLogicalNetwork}
                    disabled={!virtualNetwork || loadingLocationData}
                />

                <Form.Item
                    label={<InstanceTagsLabel />}
                >
                    <KeyValuePairs
                        dataSource={instanceTags}
                        onChange={setInstanceTags}
                        keyPlaceholder="key"
                        valuePlaceholder="value"
                        instructionMessage="Click the + icon or press Enter to add a tag. You can add up to 50 tags."
                        limit={50}
                        keyValidation={{ maxLength: 127 }}
                        valueValidation={{ maxLength: 255 }}
                    />
                </Form.Item>

                <Form.Item
                    label={
                        <span>
                            VM Name{' '}
                            <Tooltip title="VM Name of the Migration Appliance.">
                                <QuestionCircleOutlined />
                            </Tooltip>
                        </span>
                    }
                    name="vmName"
                    rules={[
                        { required: true, message: 'Please enter a VM name' },
                        { max: 64, message: 'VM name should not be more than 64 characters' },
                    ]}
                >
                    <Input placeholder="Enter a VM Name" />
                </Form.Item>

                <VMBasedMigrationsConfig />

                <CommonApplianceConfiguration {...commonConfigProps} />

                <div className="modal-footer">
                    <Button type="default" onClick={onCancel}>
                        Cancel
                    </Button>
                    <Button type="primary" htmlType="submit" loading={submitting}>
                        Initiate Migration Appliance checks
                    </Button>
                </div>
            </Form>
        </div>
    );
};
